import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Avatar,
  IconButton,
  useDisclosure,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Grid,
  GridItem,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  useColorModeValue,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useBreakpointValue,
  Collapse,
  Icon,
  Tooltip,
} from "@chakra-ui/react";
import {
  FiSearch,
  FiEdit,
  FiUserPlus,
  FiShield,
  FiUsers,
  FiSettings,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import { rbacService } from "../rbacService";

const RBAC = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    rolesCount: 0,
    adminsCount: 0,
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();
  const cancelRef = React.useRef();
  const toast = useToast();

  // Responsive layout
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Dark mode colors
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const tableHeaderBg = useColorModeValue("gray.50", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const modalBg = useColorModeValue("white", "gray.900");
  const modalHeaderBg = useColorModeValue("white", "gray.800");
  const modalBodyBg = useColorModeValue("gray.25", "gray.750");
  const searchIconColor = useColorModeValue("gray.500", "gray.400");

  const roleDefinitions = rbacService.getRoleDefinitions();

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const userData = await rbacService.getAllUsers();
      setUsers(userData);
      calculateStats(userData);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast({
        title: "Error loading users",
        description: "Could not load user data",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (userData) => {
    const total = userData.length;
    const active = userData.filter((user) => user.status === "Active").length;
    const roles = new Set(userData.map((user) => user.role)).size;
    const admins = userData.filter((user) =>
      ["SuperAdmin", "Admin", "HRHead", "ITAdmin"].includes(user.role)
    ).length;

    setStats({
      totalUsers: total,
      activeUsers: active,
      rolesCount: roles,
      adminsCount: admins,
    });
  };

  const filterUsers = () => {
    let filtered = users;

    if (searchTerm) {
      filtered = filtered.filter(
        (user) =>
          user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.employeeId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (roleFilter) {
      filtered = filtered.filter((user) => user.role === roleFilter);
    }

    setFilteredUsers(filtered);
  };

  const handleEditRole = (user) => {
    setSelectedUser(user);
    onOpen();
  };

  const handleRoleClick = (role) => {
    setSelectedRole(role);
    onConfirmOpen();
  };

  const handleRoleUpdate = async () => {
    try {
      await rbacService.updateUserRole(selectedUser._id, {
        role: selectedRole,
      });
      toast({
        title: "Role updated",
        description: `${
          selectedUser.name
        }'s role has been updated to ${getRoleDisplayName(selectedRole)}`,
        status: "success",
        duration: 3000,
      });
      fetchUsers(); // Refresh data
      onConfirmClose();
      onClose();
    } catch (error) {
      toast({
        title: "Error updating role",
        description: "Could not update user role",
        status: "error",
        duration: 3000,
      });
    }
  };

  const getRoleBadgeColor = (role) => {
    return roleDefinitions[role]?.color || "gray";
  };

  const getRoleDisplayName = (role) => {
    return roleDefinitions[role]?.name || role;
  };

  const getAvailableRoles = () => {
    return Object.keys(roleDefinitions);
  };

  const toggleCardExpansion = (userId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  // Mobile User Card Component
  const MobileUserCard = ({ user }) => {
    const isExpanded = expandedCards[user._id];
    const permissions = roleDefinitions[user.role]?.permissions || [];

    return (
      <Card
        bg={cardBg}
        borderColor={borderColor}
        border="0px"
        mb={3}
        _hover={{ shadow: "md", borderColor: "blue.500" }}
      >
        <CardBody p={4}>
          <VStack align="stretch" spacing={3}>
            {/* Header Row */}
            <HStack justify="space-between">
              <HStack spacing={3} flex="1" minW={0}>
                <Avatar name={user.name} size="sm" />
                <VStack align="start" spacing={0} flex="1" minW={0}>
                  <Text
                    fontWeight="semibold"
                    fontSize="sm"
                    color={textColor}
                    noOfLines={1}
                  >
                    {user.name}
                  </Text>
                  <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                    {user.email}
                  </Text>
                </VStack>
              </HStack>
              <Badge
                colorScheme={user.status === "Active" ? "green" : "red"}
                fontSize="xs"
                px={2}
              >
                {user.status || "Active"}
              </Badge>
            </HStack>

            {/* ID and Role */}
            <HStack justify="space-between" fontSize="xs">
              <HStack spacing={2}>
                <Text color={mutedColor}>ID:</Text>
                <Text color={textColor}>{user.employeeId}</Text>
              </HStack>
              <Badge
                colorScheme={getRoleBadgeColor(user.role)}
                variant="subtle"
                fontSize="xs"
              >
                {getRoleDisplayName(user.role)}
              </Badge>
            </HStack>

            {/* Permissions Summary */}
            <Box fontSize="xs">
              <HStack justify="space-between" mb={1}>
                <Text color={mutedColor}>Permissions:</Text>
                <IconButton
                  icon={isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                  size="xs"
                  variant="ghost"
                  onClick={() => toggleCardExpansion(user._id)}
                  aria-label="Toggle permissions"
                />
              </HStack>
              <Collapse in={!isExpanded} animateOpacity>
                <Text color={textColor} noOfLines={1}>
                  {permissions.slice(0, 2).join(", ")}
                  {permissions.length > 2 && ` +${permissions.length - 2} more`}
                </Text>
              </Collapse>
              <Collapse in={isExpanded} animateOpacity>
                <VStack align="start" spacing={1} pt={1}>
                  {permissions.map((perm, index) => (
                    <Text key={index} color={textColor}>
                      • {perm}
                    </Text>
                  ))}
                </VStack>
              </Collapse>
            </Box>

            {/* Footer */}
            <HStack
              justify="space-between"
              pt={2}
              borderTop="1px"
              borderColor={borderColor}
            >
              <Text fontSize="xs" color={mutedColor}>
                Updated: {new Date(user.updatedAt).toLocaleDateString()}
              </Text>
              <Tooltip label="Edit Role">
                <IconButton
                  icon={<FiEdit />}
                  size="sm"
                  variant="ghost"
                  colorScheme="blue"
                  onClick={() => handleEditRole(user)}
                  aria-label="Edit role"
                  minW="44px"
                  minH="44px"
                />
              </Tooltip>
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box textAlign="center" py={12}>
        <Spinner size="xl" color="blue.500" mb={4} />
        <Text>Loading RBAC management...</Text>
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }} bg={bgColor} minH="100vh" maxW="100%" mx="auto">
      <VStack spacing={{ base: 4, md: 6 }} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <Box flex="1" minW={0}>
            <Text
              fontSize={{ base: "xl", md: "2xl" }}
              fontWeight="bold"
              color={textColor}
            >
              {isMobile ? "RBAC" : "Role-Based Access Control (RBAC)"}
            </Text>
            <Text
              color={mutedColor}
              fontSize={{ base: "xs", md: "sm" }}
              noOfLines={{ base: 1, md: 2 }}
            >
              Manage user roles and permissions across the system
            </Text>
          </Box>
          {isMobile ? (
            <IconButton
              icon={<FiUserPlus />}
              colorScheme="blue"
              aria-label="Add User"
              minW="44px"
              minH="44px"
            />
          ) : (
            <Button leftIcon={<FiUserPlus />} colorScheme="blue">
              Add User
            </Button>
          )}
        </HStack>

        {/* Statistics Cards - Horizontal scroll on mobile */}
        <Box
          overflowX="auto"
          css={{
            "&::-webkit-scrollbar": {
              height: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "gray.300",
              borderRadius: "2px",
            },
          }}
        >
          <HStack spacing={3} minW={{ base: "max-content", md: "100%" }} pb={2}>
            <Card
              bg={cardBg}
              borderColor={borderColor}
              border="0px"
              minW={{ base: "140px", md: "auto" }}
              flex={{ base: "0 0 auto", md: "1" }}
            >
              <CardBody py={3} px={4}>
                <Stat>
                  <StatLabel fontSize="xs">
                    <HStack spacing={1}>
                      <FiUsers size={14} />
                      <Text>Total Users</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber fontSize="2xl">{stats.totalUsers}</StatNumber>
                </Stat>
              </CardBody>
            </Card>
            <Card
              bg={cardBg}
              borderColor={borderColor}
              border="0px"
              minW={{ base: "140px", md: "auto" }}
              flex={{ base: "0 0 auto", md: "1" }}
            >
              <CardBody py={3} px={4}>
                <Stat>
                  <StatLabel fontSize="xs">
                    <HStack spacing={1}>
                      <Icon as={FiShield} boxSize={3.5} />
                      <Text>Active</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber fontSize="2xl">{stats.activeUsers}</StatNumber>
                </Stat>
              </CardBody>
            </Card>
            <Card
              bg={cardBg}
              borderColor={borderColor}
              border="0px"
              minW={{ base: "140px", md: "auto" }}
              flex={{ base: "0 0 auto", md: "1" }}
            >
              <CardBody py={3} px={4}>
                <Stat>
                  <StatLabel fontSize="xs">
                    <HStack spacing={1}>
                      <Icon as={FiSettings} boxSize={3.5} />
                      <Text>Roles</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber fontSize="2xl">{stats.rolesCount}</StatNumber>
                </Stat>
              </CardBody>
            </Card>
            <Card
              bg={cardBg}
              borderColor={borderColor}
              border="0px"
              minW={{ base: "140px", md: "auto" }}
              flex={{ base: "0 0 auto", md: "1" }}
            >
              <CardBody py={3} px={4}>
                <Stat>
                  <StatLabel fontSize="xs">
                    <HStack spacing={1}>
                      <Icon as={FiShield} boxSize={3.5} />
                      <Text>Admins</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber fontSize="2xl">{stats.adminsCount}</StatNumber>
                </Stat>
              </CardBody>
            </Card>
          </HStack>
        </Box>

        {/* Filters */}
        <Card bg={cardBg} borderColor={borderColor} border="0px">
          <CardBody p={4}>
            <VStack spacing={3} align="stretch">
              {/* Search - Full width on mobile */}
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <FiSearch color={searchIconColor} />
                </InputLeftElement>
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  minH="44px"
                />
              </InputGroup>

              {/* Role Filter and Clear */}
              <HStack spacing={3}>
                <Select
                  placeholder="All Roles"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  flex="1"
                  minH="44px"
                >
                  {getAvailableRoles().map((role) => (
                    <option key={role} value={role}>
                      {getRoleDisplayName(role)}
                    </option>
                  ))}
                </Select>
                {!isMobile && (
                  <Button
                    leftIcon={<FiSettings />}
                    variant="outline"
                    onClick={() => {
                      setSearchTerm("");
                      setRoleFilter("");
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
                {isMobile && (searchTerm || roleFilter) && (
                  <IconButton
                    icon={<FiSettings />}
                    variant="outline"
                    onClick={() => {
                      setSearchTerm("");
                      setRoleFilter("");
                    }}
                    aria-label="Clear filters"
                    minW="44px"
                    minH="44px"
                  />
                )}
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Users - Card view on mobile, Table on desktop */}
        {isMobile ? (
          <VStack spacing={3} align="stretch">
            {filteredUsers.map((user) => (
              <MobileUserCard key={user._id} user={user} />
            ))}
            {filteredUsers.length === 0 && (
              <Box textAlign="center" py={8}>
                <Text color={mutedColor}>
                  {users.length === 0
                    ? "No users found"
                    : "No users match your filters"}
                </Text>
              </Box>
            )}
          </VStack>
        ) : (
          <Card bg={cardBg} borderColor={borderColor} border="0px">
            <CardBody p={0}>
              <Box overflowX="auto">
                <Table variant="simple">
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      <Th>User</Th>
                      <Th>Role</Th>
                      <Th>Permissions</Th>
                      <Th>Status</Th>
                      <Th>Last Updated</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredUsers.map((user) => (
                      <Tr
                        key={user._id}
                        _hover={{ bg: hoverBg }}
                        transition="background 0.2s"
                      >
                        <Td>
                          <HStack spacing={3}>
                            <Avatar name={user.name} size="sm" />
                            <Box>
                              <Text fontWeight="medium">{user.name}</Text>
                              <Text fontSize="sm" color={mutedColor}>
                                {user.email}
                              </Text>
                              <Text fontSize="xs" color={mutedColor}>
                                ID: {user.employeeId}
                              </Text>
                            </Box>
                          </HStack>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={getRoleBadgeColor(user.role)}
                            variant="subtle"
                          >
                            {getRoleDisplayName(user.role)}
                          </Badge>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            {roleDefinitions[user.role]?.permissions
                              .slice(0, 2)
                              .map((perm, index) => (
                                <Text
                                  key={index}
                                  fontSize="xs"
                                  color={mutedColor}
                                >
                                  • {perm}
                                </Text>
                              ))}
                            {roleDefinitions[user.role]?.permissions.length >
                              2 && (
                              <Text fontSize="xs" color={mutedColor}>
                                +
                                {roleDefinitions[user.role].permissions.length -
                                  2}{" "}
                                more
                              </Text>
                            )}
                          </VStack>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={
                              user.status === "Active" ? "green" : "red"
                            }
                            variant="subtle"
                          >
                            {user.status || "Active"}
                          </Badge>
                        </Td>
                        <Td>
                          <Text fontSize="sm" color={mutedColor}>
                            {new Date(user.updatedAt).toLocaleDateString()}
                          </Text>
                        </Td>
                        <Td>
                          <IconButton
                            icon={<FiEdit />}
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditRole(user)}
                            title="Edit Role"
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>

                {filteredUsers.length === 0 && (
                  <Box textAlign="center" py={8}>
                    <Text color={mutedColor}>
                      {users.length === 0
                        ? "No users found"
                        : "No users match your filters"}
                    </Text>
                  </Box>
                )}
              </Box>
            </CardBody>
          </Card>
        )}
      </VStack>

      {/* Role Edit Modal */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={{ base: "full", md: "xl" }}
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
        <ModalContent
          borderRadius={{ base: "0", md: "xl" }}
          maxW={{ base: "100%", md: "600px" }}
          bg={modalBg}
        >
          <ModalHeader
            py={{ base: 4, md: 5 }}
            px={{ base: 4, md: 6 }}
            borderBottom="2px"
            borderColor={borderColor}
            bg={modalHeaderBg}
          >
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              fontWeight="700"
              color={textColor}
              letterSpacing="-0.02em"
            >
              Change User Role
            </Text>
            <Text fontSize="sm" fontWeight="400" color={mutedColor} mt={1}>
              Update permissions and access level
            </Text>
          </ModalHeader>
          <ModalCloseButton
            top={{ base: 4, md: 5 }}
            right={{ base: 4, md: 6 }}
            minW="44px"
            minH="44px"
            _focus={{ boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.6)" }}
          />

          <ModalBody
            py={{ base: 4, md: 6 }}
            px={{ base: 4, md: 6 }}
            bg={modalBodyBg}
            maxH={{ base: "calc(100vh - 180px)", md: "60vh" }}
            overflowY="auto"
          >
            {selectedUser && (
              <VStack spacing={5} align="stretch">
                {/* User Identity Block */}
                <Box
                  p={4}
                  bg={cardBg}
                  borderRadius="lg"
                  border="0px"
                  borderColor={borderColor}
                  boxShadow="sm"
                >
                  <HStack spacing={3}>
                    <Avatar name={selectedUser.name} size="md" bg="blue.500" />
                    <Box flex={1}>
                      <Text fontSize="md" fontWeight="700" color={textColor}>
                        {selectedUser.name}
                      </Text>
                      <Text fontSize="sm" color={mutedColor} mt={0.5}>
                        {selectedUser.email}
                      </Text>
                    </Box>
                  </HStack>
                </Box>

                {/* Divider with Label */}
                <Box position="relative" py={2}>
                  <Box
                    position="absolute"
                    top="50%"
                    w="full"
                    h="1px"
                    bg={borderColor}
                  />
                  <Text
                    position="relative"
                    display="inline-block"
                    px={3}
                    bg={modalBodyBg}
                    fontSize="xs"
                    fontWeight="700"
                    color={mutedColor}
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                  >
                    Select New Role
                  </Text>
                </Box>

                {/* Role Selection List */}
                <VStack spacing={2} align="stretch">
                  {getAvailableRoles().map((role) => {
                    const isCurrentRole = selectedUser.role === role;
                    const isPrivileged = [
                      "SuperAdmin",
                      "Admin",
                      "HRHead",
                      "ITAdmin",
                    ].includes(role);

                    return (
                      <Box
                        key={role}
                        p={3.5}
                        borderRadius="lg"
                        border="2px"
                        borderColor={isCurrentRole ? "blue.500" : borderColor}
                        bg={isCurrentRole ? "blue.50" : cardBg}
                        cursor={isCurrentRole ? "not-allowed" : "pointer"}
                        opacity={isCurrentRole ? 0.75 : 1}
                        _hover={
                          !isCurrentRole
                            ? {
                                bg: hoverBg,
                                borderColor: borderColor,
                                boxShadow: "sm",
                              }
                            : {}
                        }
                        _focus={{
                          boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.6)",
                          outline: "none",
                        }}
                        transition="all 0.15s ease"
                        onClick={() => {
                          if (!isCurrentRole) {
                            handleRoleClick(role);
                          }
                        }}
                        tabIndex={isCurrentRole ? -1 : 0}
                        role="button"
                        aria-label={`Change role to ${getRoleDisplayName(
                          role
                        )}`}
                        aria-disabled={isCurrentRole}
                      >
                        <HStack spacing={3} align="flex-start">
                          <Box
                            w="36px"
                            h="36px"
                            minW="36px"
                            borderRadius="md"
                            bg={getRoleBadgeColor(role) + ".500"}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            boxShadow="sm"
                          >
                            <Text
                              fontSize="sm"
                              fontWeight="800"
                              color="white"
                              letterSpacing="tight"
                            >
                              {getRoleDisplayName(role)
                                .substring(0, 2)
                                .toUpperCase()}
                            </Text>
                          </Box>

                          <Box flex={1} minW={0}>
                            <HStack spacing={2} align="center" mb={1}>
                              <Text
                                fontSize="md"
                                fontWeight="700"
                                color={textColor}
                                lineHeight="1.2"
                              >
                                {getRoleDisplayName(role)}
                              </Text>
                              {isCurrentRole && (
                                <Badge
                                  colorScheme="blue"
                                  fontSize="2xs"
                                  px={2}
                                  py={0.5}
                                  borderRadius="full"
                                  fontWeight="700"
                                  textTransform="uppercase"
                                  letterSpacing="0.03em"
                                >
                                  Active
                                </Badge>
                              )}
                              {isPrivileged && !isCurrentRole && (
                                <Badge
                                  colorScheme="orange"
                                  fontSize="2xs"
                                  px={2}
                                  py={0.5}
                                  borderRadius="full"
                                  fontWeight="700"
                                  variant="subtle"
                                >
                                  High Access
                                </Badge>
                              )}
                            </HStack>
                            <Text
                              fontSize="sm"
                              color={mutedColor}
                              lineHeight="1.4"
                              noOfLines={2}
                            >
                              {roleDefinitions[role].description}
                            </Text>
                          </Box>
                        </HStack>
                      </Box>
                    );
                  })}
                </VStack>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter
            py={4}
            px={{ base: 4, md: 6 }}
            borderTop="2px"
            borderColor={borderColor}
            bg={modalHeaderBg}
            justifyContent="flex-start"
            flexDirection={{ base: "column", md: "row" }}
            gap={{ base: 2, md: 0 }}
          >
            <Button
              onClick={onClose}
              size="md"
              variant="ghost"
              fontWeight="600"
              width={{ base: "100%", md: "auto" }}
              minH="44px"
              _focus={{ boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.6)" }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Enhanced Confirmation Dialog */}
      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={onConfirmClose}
        isCentered
      >
        <AlertDialogOverlay bg="blackAlpha.700" backdropFilter="blur(4px)">
          <AlertDialogContent
            borderRadius={{ base: "xl", md: "xl" }}
            mx={4}
            maxW={{ base: "calc(100% - 32px)", md: "480px" }}
            bg={modalBg}
          >
            <AlertDialogHeader
              fontSize="lg"
              fontWeight="700"
              borderBottom="2px"
              borderColor={borderColor}
              py={{ base: 4, md: 5 }}
              px={{ base: 4, md: 6 }}
            >
              <HStack spacing={3}>
                <Box p={2} bg="orange.100" borderRadius="md" color="orange.600">
                  <FiShield size={20} />
                </Box>
                <Text>Confirm Role Change</Text>
              </HStack>
            </AlertDialogHeader>

            <AlertDialogBody py={{ base: 4, md: 6 }} px={{ base: 4, md: 6 }}>
              {selectedUser && selectedRole && (
                <VStack align="stretch" spacing={5}>
                  <Box>
                    <Text fontSize="sm" color={mutedColor} lineHeight="1.6">
                      You are about to change{" "}
                      <Text as="span" fontWeight="700" color={textColor}>
                        {selectedUser.name}'s
                      </Text>{" "}
                      role. This will immediately affect their access and
                      permissions.
                    </Text>
                  </Box>

                  <Box
                    p={4}
                    bg={hoverBg}
                    borderRadius="lg"
                    border="0px"
                    borderColor={borderColor}
                  >
                    <HStack spacing={4} justify="center" align="center">
                      <VStack spacing={1}>
                        <Text
                          fontSize="xs"
                          fontWeight="600"
                          color="gray.500"
                          textTransform="uppercase"
                        >
                          Current
                        </Text>
                        <Badge
                          colorScheme={getRoleBadgeColor(selectedUser.role)}
                          fontSize="sm"
                          px={3}
                          py={1.5}
                          borderRadius="md"
                          fontWeight="700"
                        >
                          {getRoleDisplayName(selectedUser.role)}
                        </Badge>
                      </VStack>

                      <Text fontSize="2xl" color="gray.400" fontWeight="300">
                        →
                      </Text>

                      <VStack spacing={1}>
                        <Text
                          fontSize="xs"
                          fontWeight="600"
                          color="gray.500"
                          textTransform="uppercase"
                        >
                          New
                        </Text>
                        <Badge
                          colorScheme={getRoleBadgeColor(selectedRole)}
                          fontSize="sm"
                          px={3}
                          py={1.5}
                          borderRadius="md"
                          fontWeight="700"
                        >
                          {getRoleDisplayName(selectedRole)}
                        </Badge>
                      </VStack>
                    </HStack>
                  </Box>

                  {["SuperAdmin", "Admin", "HRHead", "ITAdmin"].includes(
                    selectedRole
                  ) && (
                    <Alert
                      status="warning"
                      borderRadius="lg"
                      variant="left-accent"
                      bg="orange.50"
                      borderColor="orange.200"
                    >
                      <AlertIcon color="orange.500" />
                      <Box>
                        <AlertTitle
                          fontSize="sm"
                          fontWeight="700"
                          color="orange.900"
                        >
                          High-Level Access
                        </AlertTitle>
                        <AlertDescription
                          fontSize="xs"
                          color="orange.800"
                          mt={1}
                        >
                          This role has elevated privileges. Ensure this user
                          requires administrative access.
                        </AlertDescription>
                      </Box>
                    </Alert>
                  )}
                </VStack>
              )}
            </AlertDialogBody>

            <AlertDialogFooter
              borderTop="2px"
              borderColor={borderColor}
              py={4}
              px={{ base: 4, md: 6 }}
              bg={modalHeaderBg}
              flexDirection={{ base: "column", md: "row" }}
              gap={{ base: 2, md: 3 }}
            >
              <Button
                ref={cancelRef}
                onClick={onConfirmClose}
                size="md"
                variant="ghost"
                fontWeight="600"
                width={{ base: "100%", md: "auto" }}
                minH="44px"
                _focus={{ boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.6)" }}
              >
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleRoleUpdate}
                size="md"
                fontWeight="600"
                leftIcon={<FiShield />}
                width={{ base: "100%", md: "auto" }}
                minH="44px"
                _focus={{ boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.6)" }}
              >
                Confirm Change
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default RBAC;
